import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  Button,
  Crumbs,
  FieldSelect,
  FieldText,
  PageHead,
  SectionCard,
  Toggle,
  ValidityDateRangePicker,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup, openSuccessAlert } from "state/application/applicationSlice";
import {
  businessValidityApiSlice,
  useGetManagedVersionsQuery,
} from "state/businessValidity/businessValidityApiSlice";
import {
  useUpdateCommunityMutation,
  useUpdateEMSConfigMutation,
} from "state/communities/communitiesApiSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useUpdateMemberByIdMutation } from "state/members/membersApiSlice";
import {
  selectAllMeters,
  useGetMetersByCommunityIdQuery,
  useUpdateMeterByIdMutation,
} from "state/meters/metersApiSlice";
import { useUpdateOwnershipByMemberIdResourceIdMutation } from "state/ownerships/ownershipsApiSlice";
import {
  useGetAvailableResourceMetersQuery,
  useUpdateResourceByIdMutation,
} from "state/resources/resourcesApiSlice";
import { useUpdateRespByIdMutation } from "state/resp/respApiSlice";
import { useUpdateFixedBilateralPriceMutation } from "state/bilateralPrices/bilateralPricesApiSlice";
import { defaultBusinessEndDate, lisbonToday } from "utils/businessValidity";
import { DETAIL_FIELDS } from "pages/ManageResources/Single/Details";

import { formatVersionDate, getVersionEntityMeta } from "../registry";

const normalizedEntityIds = (entityId) => decodeURIComponent(entityId).split("~");

const sourceVersionForNew = (versions) => {
  const today = lisbonToday();
  const ordered = [...versions].sort((a, b) => b.start_date.localeCompare(a.start_date));
  return (
    ordered.find(
      (version) => version.start_date <= today && (!version.end_date || version.end_date >= today)
    ) ||
    ordered.find((version) => version.start_date <= today) ||
    ordered[0]
  );
};

function VersionEditorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(["common", "resources"]);
  const { entityType, entityId, versionId } = useParams();
  const community = useSelector(selectCurrentCommunity);
  const meta = useMemo(() => getVersionEntityMeta(entityType, t), [entityType, t]);
  const isEdit = !!versionId;
  const returnTo = location.state?.returnTo;
  const label = location.state?.label;
  const [primaryId, secondaryId] = normalizedEntityIds(entityId);

  const { data: versions = [], isFetching } = useGetManagedVersionsQuery(
    { communityId: community.id, entityType, entityId },
    { refetchOnMountOrArgChange: true }
  );
  const { meters } = useGetMetersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "false" },
    {
      selectFromResult: ({ data }) => ({
        meters: (data && selectAllMeters(data)) || [],
      }),
    }
  );

  const source = useMemo(
    () =>
      isEdit ? versions.find((version) => version.id === versionId) : sourceVersionForNew(versions),
    [isEdit, versionId, versions]
  );

  const resourceFields = useMemo(
    () => (entityType === "resource" ? DETAIL_FIELDS[source?.resource_type] || [] : []),
    [entityType, source?.resource_type]
  );

  const [startDate, setStartDate] = useState(lisbonToday());
  const [endDate, setEndDate] = useState(() => defaultBusinessEndDate(lisbonToday()));
  const [values, setValues] = useState({});
  const { data: availableResourceMeters = [], isFetching: loadingAvailableResourceMeters } =
    useGetAvailableResourceMetersQuery(
      {
        communityId: community.id,
        startDate,
        endDate: endDate || undefined,
        resourceId: primaryId,
      },
      { skip: entityType !== "resource" || !startDate }
    );

  useEffect(() => {
    if (!source) return;
    const next = {};
    meta.fields.forEach((field) => {
      next[field.key] =
        field.type === "json"
          ? JSON.stringify(source[field.key] || {}, null, 2)
          : source[field.key] ?? (field.type === "boolean" ? false : "");
    });
    resourceFields.forEach((field) => {
      next[field.key] = source.details?.[field.key] ?? (field.input ? "" : 0);
    });
    setValues(next);
    if (isEdit) {
      setStartDate(source.start_date);
      setEndDate(source.end_date || "");
    } else {
      const start = lisbonToday();
      setStartDate(start);
      setEndDate(
        source.end_date && source.end_date >= start
          ? source.end_date
          : defaultBusinessEndDate(start)
      );
    }
  }, [isEdit, meta?.fields, resourceFields, source]);

  const [updateCommunity, { isLoading: savingCommunity }] = useUpdateCommunityMutation();
  const [updateMember, { isLoading: savingMember }] = useUpdateMemberByIdMutation();
  const [updateMeter, { isLoading: savingMeter }] = useUpdateMeterByIdMutation();
  const [updateResource, { isLoading: savingResource }] = useUpdateResourceByIdMutation();
  const [updateOwnership, { isLoading: savingOwnership }] =
    useUpdateOwnershipByMemberIdResourceIdMutation();
  const [updateResp, { isLoading: savingResp }] = useUpdateRespByIdMutation();
  const [updateFixed, { isLoading: savingFixed }] = useUpdateFixedBilateralPriceMutation();
  const [updateEms, { isLoading: savingEms }] = useUpdateEMSConfigMutation();
  const isSaving =
    savingCommunity ||
    savingMember ||
    savingMeter ||
    savingResource ||
    savingOwnership ||
    savingResp ||
    savingFixed ||
    savingEms;

  useEffect(() => {
    if (entityType !== "contract" || !community.id || (isEdit && !source)) return;
    navigate(
      `/communities/${community.id}/infrastructure/contracts/${isEdit ? versionId : "new"}`,
      {
        replace: true,
        state: {
          ...location.state,
          meterId: primaryId,
          meterIdentifier: label,
          action: isEdit ? "edit" : "new",
          effectiveAt: source?.start_date,
        },
      }
    );
  }, [
    community.id,
    entityType,
    isEdit,
    label,
    location.state,
    navigate,
    primaryId,
    source,
    versionId,
  ]);

  const historyPath = `/communities/${community.id}/versions/${entityType}/${encodeURIComponent(
    entityId
  )}`;

  const normalizedValues = () =>
    Object.fromEntries(
      meta.fields.map((field) => {
        let value = values[field.key];
        if (field.type === "number") value = value === "" ? null : Number(value);
        if (field.type === "json") value = JSON.parse(value || "{}");
        return [field.key, value === "" ? null : value];
      })
    );

  const save = async () => {
    if (!source || !startDate || (endDate && endDate < startDate)) return;
    try {
      const payload = normalizedValues();
      const validity = { start_date: startDate, end_date: endDate || null };
      if (entityType === "config") {
        await updateCommunity({ id: community.id, ...payload, ...validity }).unwrap();
      } else if (entityType === "membership") {
        await updateMember({
          communityId: community.id,
          memberId: primaryId,
          data: { ...payload, ...validity },
        }).unwrap();
      } else if (entityType === "meter") {
        if (source.meter_type !== "INTERNAL") payload.cpe_id = null;
        await updateMeter({
          id: primaryId,
          communityId: community.id,
          newMeter: {
            identifier: source.identifier,
            type: source.meter_type,
            ...payload,
            ...validity,
          },
        }).unwrap();
      } else if (entityType === "resource") {
        const details = Object.fromEntries(
          resourceFields.map((field) => [
            field.key,
            field.input === "text" || field.input === "select"
              ? values[field.key]
              : Number(values[field.key]),
          ])
        );
        await updateResource({
          communityId: community.id,
          assetId: primaryId,
          newAsset: {
            ...payload,
            type: source.resource_type,
            details,
            ...validity,
          },
        }).unwrap();
      } else if (entityType === "ownership") {
        await updateOwnership({
          communityId: community.id,
          resourceId: primaryId,
          memberId: secondaryId,
          percOwnership: payload.perc_ownership,
          ...validity,
        }).unwrap();
      } else if (entityType === "resp") {
        await updateResp({
          communityId: community.id,
          assetId: primaryId,
          effectiveAt: startDate,
          newAsset: {
            provider_meter_id: source.provider_meter_id,
            receiver_meter_id: source.receiver_meter_id,
            community_id: community.id,
            is_active: payload.is_active,
            ...validity,
          },
        }).unwrap();
      } else if (entityType === "fixedBilateral") {
        await updateFixed({
          communityId: community.id,
          receiverUserId: primaryId,
          providerUserId: secondaryId,
          price: payload.price,
          startDate,
          endDate: endDate || null,
        }).unwrap();
      } else if (entityType === "ems") {
        await updateEms({
          communityId: community.id,
          configId: primaryId,
          configData: { ...payload, ...validity },
        }).unwrap();
      }
      dispatch(
        businessValidityApiSlice.util.invalidateTags([
          { type: "BusinessValidity", id: `${entityType}:${entityId}` },
        ])
      );
      dispatch(
        openSuccessAlert({
          title: isEdit
            ? t("versioning.editor.success.updatedTitle")
            : t("versioning.editor.success.addedTitle"),
          description: t("versioning.editor.success.description", {
            entity: meta.singular,
            startDate: formatVersionDate(startDate, i18n.resolvedLanguage),
            endDate:
              formatVersionDate(endDate, i18n.resolvedLanguage) ||
              t("versioning.editor.success.openEnd"),
          }),
        })
      );
      navigate(historyPath, { state: { returnTo, label } });
    } catch (error) {
      dispatch(openNotificationPopup({ notification: error, type: "error" }));
    }
  };

  if (entityType === "contract" || !meta || (!source && !isFetching)) return null;

  const meterOptions = [
    { value: "", label: t("versioning.editor.none") },
    ...meters.map((meter) => ({ value: meter.id, label: meter.identifier })),
  ];
  const parentCpeOptions = [
    { value: "", label: t("versioning.editor.none") },
    ...meters
      .filter((meter) => meter.type === "CPE")
      .map((meter) => ({ value: meter.id, label: meter.identifier })),
  ];
  const resourceMeterOptions = [
    { value: "", label: t("versioning.editor.noneViewOnly") },
    ...availableResourceMeters.map((meter) => ({
      value: meter.id,
      label: `${meter.identifier} · ${meter.type}`,
    })),
  ];
  const resourceMeterSelectionIsValid =
    entityType !== "resource" ||
    !values.meter_id ||
    availableResourceMeters.some((meter) => meter.id === values.meter_id);
  const renderField = (field) => {
    if (entityType === "meter" && field.key === "cpe_id" && source?.meter_type !== "INTERNAL") {
      return null;
    }
    if (field.type === "boolean") {
      return (
        <div key={field.key} className="rec-toggle-row">
          <span className="flex-1 text-[13.5px] font-semibold text-ink">{field.label}</span>
          <Toggle
            on={!!values[field.key]}
            ariaLabel={field.label}
            onClick={() =>
              setValues((current) => ({ ...current, [field.key]: !current[field.key] }))
            }
          />
        </div>
      );
    }
    if (field.type === "json") {
      return (
        <div key={field.key} className="rec-field sm:col-span-2">
          <label htmlFor={`version-${field.key}`}>{field.label}</label>
          <textarea
            id={`version-${field.key}`}
            className="rec-input min-h-[180px] font-[family-name:var(--mono)]"
            value={values[field.key] ?? ""}
            onChange={(event) =>
              setValues((current) => ({ ...current, [field.key]: event.target.value }))
            }
          />
        </div>
      );
    }
    if (field.type === "status") {
      return (
        <FieldSelect
          key={field.key}
          label={field.label}
          value={values[field.key] ?? ""}
          options={[
            { value: "ACTIVE", label: t("versioning.optionValues.ACTIVE") },
            { value: "INACTIVE", label: t("versioning.optionValues.INACTIVE") },
          ]}
          onChange={(value) => setValues((current) => ({ ...current, [field.key]: value }))}
        />
      );
    }
    if (field.type === "meter" || field.input === "select") {
      let { options } = field;
      if (field.type === "meter") options = meterOptions;
      if (entityType === "meter" && field.key === "cpe_id") options = parentCpeOptions;
      if (entityType === "resource" && field.key === "meter_id") {
        options = resourceMeterOptions;
      }
      return (
        <FieldSelect
          key={field.key}
          label={field.label}
          value={values[field.key] ?? ""}
          options={options}
          onChange={(value) => setValues((current) => ({ ...current, [field.key]: value }))}
        />
      );
    }
    return (
      <FieldText
        key={field.key}
        label={field.label}
        type={field.type === "number" || !field.input ? field.type || "text" : field.input}
        value={values[field.key] ?? ""}
        onChange={(value) => setValues((current) => ({ ...current, [field.key]: value }))}
      />
    );
  };

  return (
    <div>
      <Crumbs
        items={[
          { label: community?.name || "—" },
          { label: label || meta.title, to: historyPath },
          {
            label: isEdit
              ? t("versioning.editor.editVersion")
              : t("versioning.editor.addNewVersion"),
          },
        ]}
      />
      <PageHead
        title={
          isEdit
            ? t("versioning.editor.editTitle", { entity: meta.singular })
            : t("versioning.editor.addTitle", { entity: meta.singular })
        }
        subtitle={isEdit ? t("versioning.editor.editSubtitle") : t("versioning.editor.addSubtitle")}
        actions={
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate(historyPath, { state: { returnTo, label } })}
            >
              {t("versioning.editor.cancel")}
            </Button>
            <Button
              disabled={
                isSaving ||
                !source ||
                !startDate ||
                (endDate && endDate < startDate) ||
                loadingAvailableResourceMeters ||
                !resourceMeterSelectionIsValid
              }
              onClick={save}
            >
              <Icon name="save" />
              {isEdit ? t("versioning.editor.saveRevision") : t("versioning.editor.addVersion")}
            </Button>
          </div>
        }
      />

      <div className="flex flex-col gap-rec">
        <SectionCard icon="date_range" title={t("versioning.editor.validityPeriod")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ValidityDateRangePicker
              className="sm:col-span-2"
              value={{ start: startDate, end: endDate }}
              onChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
              disabled={isEdit}
            />
          </div>
          {isEdit && (
            <p className="text-[12.5px] text-ink-3 mb-0 mt-3">
              {t("versioning.editor.boundariesFixed")}
            </p>
          )}
        </SectionCard>

        <SectionCard icon={meta.icon} title={t("versioning.editor.operationalConfiguration")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {meta.fields.map(renderField)}
            {resourceFields.map((field) =>
              renderField({
                ...field,
                label: t(`new_resource.form.${field.labelKey}.label`, { ns: "resources" }),
                type: field.input === "text" || field.input === "select" ? undefined : "number",
              })
            )}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

export default VersionEditorPage;
